import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core'
import { UserReviewClient } from 'src/app/shared/services/ppv.api'

export interface ReviewTypeStats {
   total: number
   completed: number
   revieweeSubmitted: number
   reviewerSubmitted: number
   completePct: number
   revieweePct: number
   reviewerPct: number
}

export interface ReviewTypeRow {
   label: string
   icon: string
   cycleLabel: string
   reviewerLabel: string
   stats: ReviewTypeStats
   open: boolean
}

export interface PendingDeferral {
   id: number
   revieweeName: string
   reason: string
   role: string
   requestedOn: Date | string
}

@Component({
   selector: 'app-home-dashboard',
   templateUrl: './home-dashboard.component.html',
   styleUrls: ['./home-dashboard.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class HomeDashboardComponent implements OnInit {
   @Input() cycleConfig: Record<string, any> = {}
   @Output() navigateToDeferrals = new EventEmitter<void>()

   loading = true

   dvmStats: ReviewTypeStats = this.emptyStats()
   hmStats: ReviewTypeStats = this.emptyStats()
   cosStats: ReviewTypeStats = this.emptyStats()
   rmStats: ReviewTypeStats = this.emptyStats()
   doStats: ReviewTypeStats = this.emptyStats()
   teamStats: ReviewTypeStats = this.emptyStats()

   private teamReviews: any[] = []

   reviewTypeRows: ReviewTypeRow[] = []

   totalDeferred = 0
   pendingCount = 0
   reEnabledCount = 0
   pendingRequests: PendingDeferral[] = []

   private readonly AVATAR_PALETTES = [
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#26b5ae' },
      { bg: '#ffefcc', fg: '#976a12' },
      { bg: '#fdebe4', fg: '#f05d44' },
      { bg: '#e9dee9', fg: '#831183' },
      { bg: '#fae8e8', fg: '#d80c47' },
   ]

   constructor(private userReviewClient: UserReviewClient) {}

   ngOnInit(): void {
      this.loadAll()
   }

   private loadAll(): void {
      this.loading = true

      // Load all review types in parallel
      Promise.all([
         //this.loadReviewTypeStats('DVM', undefined),
         this.loadReviewTypeStats('DVM', 'Q3'),
         this.loadReviewTypeStats('HM', undefined),
         this.loadReviewTypeStats('COS', undefined),
         this.loadReviewTypeStats('RM', undefined),
         this.loadReviewTypeStats('DO', undefined),
         this.loadReviewTypeStats('CROSSTRAINED', undefined),
         this.loadReviewTypeStats('CLINICAL', undefined),
         this.loadReviewTypeStats('RECEPTION', undefined),
         this.loadDeferralStats(),
      ]).then(() => {
         this.buildReviewTypeRows()
         this.loading = false
      })
   }

   private loadReviewTypeStats(reviewType: string, cycle: string | undefined): Promise<void> {
      return new Promise((resolve) => {
         this.userReviewClient.getUserReviewsAll(reviewType, cycle).subscribe({
            next: (reviews) => {
               const stats = this.calculateStats(reviews)
               switch (reviewType) {
                  case 'DVM':
                     this.dvmStats = stats
                     break
                  case 'HM':
                     this.hmStats = stats
                     break
                  case 'COS':
                     this.cosStats = stats
                     break
                  case 'RM':
                     this.rmStats = stats
                     break
                  case 'DO':
                     this.doStats = stats
                     break
                  case 'CROSSTRAINED':
                  case 'CLINICAL':
                  case 'RECEPTION':
                     this.teamReviews = [...(this.teamReviews ?? []), ...reviews]
                     this.teamStats = this.calculateStats(this.teamReviews)
                     break
               }
               resolve()
            },
            error: () => resolve(),
         })
      })
   }

   private calculateStats(reviews: any[]): ReviewTypeStats {
      const total = reviews.length
      const completed = reviews.filter((r) => r.isCompleted).length
      const revieweeSubmitted = reviews.filter((r) => r.isDVMSubmitted || r.isRevieweeSubmitted).length
      const reviewerSubmitted = reviews.filter((r) => r.isCOSSubmitted || r.isReviewerSubmitted).length

      return {
         total,
         completed,
         revieweeSubmitted,
         reviewerSubmitted,
         completePct: total ? Math.round((completed / total) * 100) : 0,
         revieweePct: total ? Math.round((revieweeSubmitted / total) * 100) : 0,
         reviewerPct: total ? Math.round((reviewerSubmitted / total) * 100) : 0,
      }
   }

   private loadDeferralStats(): Promise<void> {
      return new Promise((resolve) => {
         this.userReviewClient.getDeferralRequests().subscribe({
            next: (data) => {
               this.totalDeferred = data.filter((r) => r.status === 'Approved' || r.status === 'Deferred').length
               this.pendingCount = data.filter((r) => r.status === 'Pending').length
               this.reEnabledCount = data.filter((r) => r.status === 'ReEnabled').length
               this.pendingRequests = data
                  .filter((r) => r.status === 'Pending')
                  .sort((a, b) => new Date(b.requestedOn).getTime() - new Date(a.requestedOn).getTime())
                  .slice(0, 5)
                  .map((r) => ({
                     id: r.id,
                     revieweeName: r.revieweeName,
                     reason: r.reason,
                     role: r.role,
                     requestedOn: r.requestedOn,
                  }))
               resolve()
            },
            error: () => resolve(),
         })
      })
   }

   private buildReviewTypeRows(): void {
      this.reviewTypeRows = [
         {
            label: 'Associate DVMs',
            icon: 'fa-stethoscope',
            //cycleLabel: this.getActiveDvmCycle(),
            cycleLabel: 'Q3',
            reviewerLabel: 'Reviewer (COS)',
            stats: this.dvmStats,
            open: this.isCycleOpen('DVM'),
         },
         {
            label: 'Hospital Managers',
            icon: 'fa-hospital-user',
            cycleLabel: '',
            reviewerLabel: 'Reviewer (RM)',
            stats: this.hmStats,
            open: this.isCycleOpen('HM'),
         },
         {
            label: 'Chiefs of Staff',
            icon: 'fa-user-doctor',
            cycleLabel: '',
            reviewerLabel: 'Reviewer (COSA)',
            stats: this.cosStats,
            open: this.isCycleOpen('COS'),
         },
         {
            label: 'Regional Managers',
            icon: 'fa-user-tie',
            cycleLabel: '',
            reviewerLabel: 'Reviewer (DO)',
            stats: this.rmStats,
            open: this.isCycleOpen('RM'),
         },
         {
            label: 'Directors of Operations',
            icon: 'fa-briefcase',
            cycleLabel: '',
            reviewerLabel: 'Reviewer (VP)',
            stats: this.doStats,
            open: this.isCycleOpen('DO'),
         },
         {
            label: 'Team Members',
            icon: 'fa-users',
            cycleLabel: '',
            reviewerLabel: 'Reviewer (HM)',
            stats: this.teamStats,
            open: this.isCycleOpen('CROSSTRAINED'),
         },
      ]
   }

   isCycleOpen(reviewType: string): boolean {
      const keys = reviewType === 'DVM' ? ['DVM_Q1', 'DVM_Q2', 'DVM_Q3', 'DVM_Q4'] : [reviewType]
      return keys.some((key) => this.cycleConfig[key]?.isEnabled)
   }

   private getActiveDvmCycle(): string {
      const active = ['Q1', 'Q2', 'Q3', 'Q4'].find((q) => this.cycleConfig[`DVM_${q}`]?.isEnabled)
      return active ? active : ''
   }

   goToDeferrals(): void {
      this.navigateToDeferrals.emit()
   }

   private emptyStats(): ReviewTypeStats {
      return { total: 0, completed: 0, revieweeSubmitted: 0, reviewerSubmitted: 0, completePct: 0, revieweePct: 0, reviewerPct: 0 }
   }

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   getAvatarBg(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].bg
   }

   getAvatarColor(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].fg
   }

   private hashName(name: string): number {
      if (!name) return 0
      return name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
   }
}
